import { makeAutoObservable, computed, runInAction } from 'mobx';
import type { Deal } from './types';
import type { RootStore } from './RootStore';

export class DealStore {
  deals = new Map<string, Deal>();
  private root: RootStore;

  // Secondary indexes (plain, not observable)
  private _contactIdIndex = new Map<string, Set<string>>();
  private _projectIdIndex = new Map<string, Set<string>>();
  private _dealTypeIdIndex = new Map<string, Set<string>>();

  constructor(root: RootStore) {
    this.root = root;
    makeAutoObservable(this, {
      all: computed,
      open: computed,
      won: computed,
      lost: computed,
    });
  }

  // ── Index helpers ──────────────────────────────────────────────────────────

  private _addToIndex(index: Map<string, Set<string>>, key: string | null | undefined, id: string): void {
    if (!key) return;
    if (!index.has(key)) index.set(key, new Set());
    index.get(key)!.add(id);
  }

  private _removeFromIndex(index: Map<string, Set<string>>, key: string | null | undefined, id: string): void {
    if (!key) return;
    index.get(key)?.delete(id);
  }

  private _set(id: string, deal: Deal): void {
    const existing = this.deals.get(id);
    if (existing) {
      if (existing.contactId !== deal.contactId) {
        this._removeFromIndex(this._contactIdIndex, existing.contactId, id);
        this._addToIndex(this._contactIdIndex, deal.contactId, id);
      }
      if (existing.projectId !== deal.projectId) {
        this._removeFromIndex(this._projectIdIndex, existing.projectId, id);
        this._addToIndex(this._projectIdIndex, deal.projectId, id);
      }
      if (existing.dealTypeId !== deal.dealTypeId) {
        this._removeFromIndex(this._dealTypeIdIndex, existing.dealTypeId, id);
        this._addToIndex(this._dealTypeIdIndex, deal.dealTypeId, id);
      }
    } else {
      this._addToIndex(this._contactIdIndex, deal.contactId, id);
      this._addToIndex(this._projectIdIndex, deal.projectId, id);
      this._addToIndex(this._dealTypeIdIndex, deal.dealTypeId, id);
    }
    this.deals.set(id, deal);
  }

  private _delete(id: string): void {
    const existing = this.deals.get(id);
    if (existing) {
      this._removeFromIndex(this._contactIdIndex, existing.contactId, id);
      this._removeFromIndex(this._projectIdIndex, existing.projectId, id);
      this._removeFromIndex(this._dealTypeIdIndex, existing.dealTypeId, id);
    }
    this.deals.delete(id);
  }

  // ── Computed ───────────────────────────────────────────────────────────────

  get all(): Deal[] {
    const result: Deal[] = [];
    for (const d of this.deals.values()) {
      if (!d.isDeleted) result.push(d);
    }
    return result.sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1));
  }

  get open(): Deal[] { return this.all.filter(d => d.status === 0); }
  get won(): Deal[] { return this.all.filter(d => d.status === 1); }
  get lost(): Deal[] { return this.all.filter(d => d.status === 2); }

  // ── Queries ────────────────────────────────────────────────────────────────

  getById(id: string): Deal | undefined {
    const deal = this.deals.get(id);
    return deal && !deal.isDeleted ? deal : undefined;
  }

  getByDealTypeId(dealTypeId: string): Deal[] {
    const ids = this._dealTypeIdIndex.get(dealTypeId);
    if (!ids || ids.size === 0) return [];
    const result: Deal[] = [];
    for (const id of ids) {
      const d = this.deals.get(id);
      if (d && !d.isDeleted) result.push(d);
    }
    return result;
  }

  getByContactId(contactId: string): Deal[] {
    const ids = this._contactIdIndex.get(contactId);
    if (!ids || ids.size === 0) return [];
    const result: Deal[] = [];
    for (const id of ids) {
      const d = this.deals.get(id);
      if (d && !d.isDeleted) result.push(d);
    }
    return result;
  }

  getByProjectId(projectId: string): Deal[] {
    const ids = this._projectIdIndex.get(projectId);
    if (!ids || ids.size === 0) return [];
    const result: Deal[] = [];
    for (const id of ids) {
      const d = this.deals.get(id);
      if (d && !d.isDeleted) result.push(d);
    }
    return result;
  }

  // ── Bootstrap ──────────────────────────────────────────────────────────────

  loadFromRecords(records: Record<string, unknown>[]): void {
    runInAction(() => {
      for (const record of records) {
        this._set(record.id as string, record as unknown as Deal);
      }
    });
  }

  // ── Sync ───────────────────────────────────────────────────────────────────

  applyChanges(changes: { id: string; data?: Record<string, unknown>; isDeleted: boolean }[]): void {
    runInAction(() => {
      for (const change of changes) {
        if (change.isDeleted) {
          this._delete(change.id);
        } else if (change.data) {
          this._set(change.id, change.data as unknown as Deal);
        }
      }
    });
  }

  removeFromMap(id: string): void {
    this._delete(id);
  }

  // ── Mutations ──────────────────────────────────────────────────────────────

  create(data: Partial<Deal>): Deal {
    const deal: Deal = {
      id: crypto.randomUUID(),
      description: data.description,
      status: data.status ?? 0,
      dealTypeId: data.dealTypeId ?? '',
      contactId: data.contactId,
      projectId: data.projectId,
      customFields: data.customFields,
      ownerId: data.ownerId,
      orgId: '',
      syncVersion: 0,
      isDeleted: false,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    this._set(deal.id, deal);
    this.root.syncStore.queueMutation('deal', 'create', deal.id, deal);
    return deal;
  }

  update(id: string, patch: Partial<Deal>): void {
    const existing = this.deals.get(id);
    if (!existing) return;
    const updated = { ...existing, ...patch, updatedAt: new Date().toISOString() };
    this._set(id, updated);
    this.root.syncStore.queueMutation('deal', 'update', id, patch);
  }

  delete(id: string): void {
    const existing = this.deals.get(id);
    if (existing) {
      this._set(id, { ...existing, isDeleted: true, deletedAt: new Date().toISOString(), updatedAt: new Date().toISOString() });
    }
    this.root.syncStore.queueMutation('deal', 'delete', id);
  }

  restore(id: string): void {
    const existing = this.deals.get(id);
    if (existing) {
      this._set(id, { ...existing, isDeleted: false, deletedAt: undefined, updatedAt: new Date().toISOString() });
      this.root.syncStore.queueMutation('deal', 'update', id, { isDeleted: false });
    }
  }

  clear(): void {
    this.deals.clear();
    this._contactIdIndex.clear();
    this._projectIdIndex.clear();
    this._dealTypeIdIndex.clear();
  }
}
